import {
  Box,
  Button,
  HStack,
  VStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  KYDEventConfirmationEmail,
  EventDetailConfirmationEmailsSection,
} from "@common/interfaces/KYDEvent";
import { BodyDoc } from "@common/interfaces/KYDCampaign";
import { useForm } from "react-hook-form";
import TipTapEditor from "../../../ui/editor/TipTapEditor";
import { useRef, useState, useEffect } from "react";
import { Editor } from "@tiptap/react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { KYDInput, KYDMultiSelect } from "src/ui";

export type ConfirmationEmailValue = {
  id?: string;
  name: string;
  note: string;
  note_rich?: BodyDoc;
  ticket_type_ids: string[];
};

const ConfirmationEmailModal = ({
  data,
  note,
  onClose,
  onSave,
  isLoading = false,
  isOpen = false,
  isPreviewMode = false,
}: {
  isOpen?: boolean;
  isPreviewMode?: boolean;
  data: EventDetailConfirmationEmailsSection["data"];
  isLoading: boolean;
  onClose: () => void;
  note: KYDEventConfirmationEmail | null;
  onSave: (note: ConfirmationEmailValue) => void;
}) => {
  const editorRef = useRef<Editor>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (isPreviewMode) {
      setPreviewOpen(true);
    }
  }, [isPreviewMode]);

  const { register, handleSubmit, setValue, control, reset, watch } =
    useForm<ConfirmationEmailValue>({
      criteriaMode: "all",
      reValidateMode: "onBlur",
      mode: "all",
      values: {
        id: note?.id,
        name: note?.name || "",
        note: note?.note || "",
        note_rich: note?.note_rich,
        ticket_type_ids: note?.ticket_type_ids || [],
      },
    });

  const currentNote = watch("note");

  const _onClose = () => {
    reset();
    onClose();
  };

  const _onSave = (values: ConfirmationEmailValue) => {
    const txt = editorRef.current?.getText() || "";
    const json = editorRef.current?.getJSON();

    // Strip trailing empty paragraph node
    const cleanJson = { ...json };
    if (cleanJson?.content?.length > 0) {
      const lastNode = cleanJson.content[cleanJson.content.length - 1];
      if (
        lastNode.type === "paragraph" &&
        (!lastNode.content || lastNode.content.length === 0)
      ) {
        cleanJson.content = cleanJson.content.slice(0, -1);
      }
    }

    onSave({
      ...values,
      note: txt,
      note_rich: cleanJson as unknown as BodyDoc,
    });
  };

  const stripTrailingEmptyParagraphs = (html: string): string => {
    return html.replace(/(<p><\/p>\s*)+$/g, "");
  };

  return (
    <Modal
      size="3xl"
      isOpen={isOpen}
      onClose={_onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="20px"
        background="#FAFAFA"
        boxShadow="0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.10);"
        margin="auto"
      >
        <ModalHeader>
          {" "}
          {isPreviewMode ? note?.name : "Confirmation Email Note"}
        </ModalHeader>
        {isPreviewMode ? <ModalCloseButton margin="10px 10px 0 0" /> : null}
        <ModalBody
          maxHeight={{ base: "90vh", md: "70vh" }}
          overflow="auto"
          sx={{ scrollbarGutter: "stable" }}
        >
          <Stack spacing={4} direction="column">
            <Stack spacing={4} flex={1}>
              <Box
                display={isPreviewMode ? "none" : "flex"}
                flexDirection="column"
                gap={4}
              >
                <KYDInput
                  label="Name"
                  placeholder="e.g., VIP Ticket Information"
                  isDisabled={isLoading}
                  variant="labeled"
                  {...register("name", { required: true })}
                />

                <Stack spacing={0}>
                  <KYDMultiSelect
                    id="ticket_type_ids"
                    label="Target Ticket Types"
                    placeholder="Select ticket type"
                    variant="labeled"
                    tooltip="Select one or more ticket types this message should apply to. Buyers who purchase any of these tickets will automatically see this additional note in their confirmation email."
                    options={data.ticket_types.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                    values={watch("ticket_type_ids").map((v) => ({
                      label:
                        data.ticket_types.find((tt) => tt.id === v)?.name || v,
                      value: v,
                    }))}
                    onChange={(vals) => {
                      setValue(
                        "ticket_type_ids",
                        vals.map((v) => v.value)
                      );
                    }}
                  />
                </Stack>

                <Stack spacing={0}>
                  <Text fontWeight={"medium"}>Message</Text>
                  <Box
                    minH={"180px"}
                    borderWidth={1}
                    borderColor={"gray.300"}
                    borderRadius={"md"}
                  >
                    <TipTapEditor
                      key={note?.id || "new"}
                      ref={editorRef}
                      value={
                        (note?.note_rich ||
                          note?.note ||
                          "") as unknown as BodyDoc
                      }
                      onChange={() => {
                        // Sync HTML to form state for preview
                        if (editorRef.current) {
                          const html = editorRef.current.getHTML();
                          const cleanedHtml =
                            stripTrailingEmptyParagraphs(html);
                          setValue("note", cleanedHtml);
                        }
                      }}
                      isDisabled={isLoading}
                    />
                  </Box>
                </Stack>
              </Box>

              <VStack
                alignItems="flex-start"
                borderWidth={1}
                borderColor={isPreviewMode ? "transparent" : "gray.300"}
                borderRadius={"md"}
                bg={isPreviewMode ? "transparent" : "white"}
                flex={1}
                px={isPreviewMode ? 0 : 4}
              >
                <HStack
                  height="54px"
                  width="100%"
                  cursor="pointer"
                  onClick={() => setPreviewOpen(!previewOpen)}
                  display={isPreviewMode ? "none" : "flex"}
                >
                  <Text fontWeight="medium">Preview</Text>
                  <Icon as={previewOpen ? FaChevronUp : FaChevronDown} />
                </HStack>
                {previewOpen && (
                  <Box
                    p={6}
                    borderWidth={1}
                    borderRadius="md"
                    borderColor={"black"}
                    bg="white"
                    boxShadow="sm"
                    width="100%"
                    my={4}
                  >
                    <Text
                      style={{
                        color: "black",
                        lineHeight: "24px",
                        fontSize: "12px",
                        margin: "0 0 8px 0",
                        textTransform: "uppercase",
                        fontWeight: "bold",
                        marginBottom: "8px",
                      }}
                    >
                      ADDITIONAL INFO FOR YOUR TICKET
                    </Text>
                    <Box
                      style={{
                        marginTop: "8px",
                        borderTop: "1px solid #333",
                        padding: "8px",
                        paddingTop: "16px",
                      }}
                    >
                      <Box
                        style={{
                          backgroundColor: "black",
                          color: "white",
                          fontSize: "14px",
                          padding: "4px 8px",
                          lineHeight: "24px",
                          borderRadius: "4px",
                          width: "fit-content",
                          textAlign: "left",
                          margin: "0 0 8px 0",
                        }}
                      >
                        <Text>{watch("name") || "NAME"}</Text>
                      </Box>
                      <Box
                        className="prose"
                        dangerouslySetInnerHTML={{
                          __html: currentNote || "",
                        }}
                        sx={{
                          fontFamily: "Helvetica",
                          "h1, h2, h3, h4, h5, h6": {
                            fontWeight: "bold",
                            marginBottom: "0.5em",
                            marginTop: "1em",
                          },
                          h1: {
                            fontSize: "2em",
                            marginTop: "20px",
                            marginBottom: "8px",
                          },
                          h2: {
                            fontSize: "1.5em",
                            marginTop: "20px",
                            marginBottom: "8px",
                          },
                          h3: {
                            fontSize: "1.17em",
                            marginTop: "20px",
                            marginBottom: "8px",
                          },
                          p: {
                            marginBottom: "1em",
                            fontSize: "16px",
                            lineHeight: "24px",
                            minHeight: "24px",
                          },
                          a: {
                            color: "blue.500",
                            textDecoration: "underline",
                          },
                          "strong, b": { fontWeight: "bold" },
                          "em, i": { fontStyle: "italic" },
                          "ul, ol": {
                            paddingLeft: "1.5em",
                            marginBottom: "1em",
                          },
                          ul: { listStyleType: "disc" },
                          ol: { listStyleType: "decimal" },
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </VStack>
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter display={isPreviewMode ? "none" : "flex"}>
          <Button isDisabled={isLoading} onClick={_onClose} mr="4">
            Cancel
          </Button>
          <Button
            color="white"
            bg="green.500"
            _hover={{
              backgroundColor: "green.600",
            }}
            minW="120px"
            isLoading={isLoading}
            onClick={handleSubmit(_onSave)}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConfirmationEmailModal;
